(function () {
    'use strict';

    angular
        .module('app')
        .controller('EmailEditCtrl', EmailEditCtrl);

    EmailEditCtrl.$inject = [
        '$scope',
        '$rootScope',
        '$timeout',
        '$location',
        '$routeParams',
        '$q',
        '$mdToast',
        'ChilliUserProfileService',
        'EmailService',
        'EmailTemplatesService',
        'EntitiesService',
        'DialogService',
        'NavigationAidService',
        'UnsavedChangesService',
        'Email',
        'WindowTitleService'];

    /**
     * @ngdoc control
     * @name EmailEditCtrl
     * 
     * @description
     * Controller which is used for the view sent/received email messages or edit draft email messages.
     */
    function EmailEditCtrl(
        $scope,
        $rootScope,
        $timeout,
        $location,
        $routeParams,
        $q,
        $mdToast,
        ChilliUserProfileService,
        EmailService,
        EmailTemplatesService,
        EntitiesService,
        DialogService,
        NavigationAidService,
        UnsavedChangesService,
        Email,
        WindowTitleService) {

        /* jshint validthis:true */
        var vm = this;
        var saveEmailDraftTimer = null;
        var emailDraftDelay = null;
        vm.disableAllButtons = false;
        vm.errorMessage = null;
        vm.loading = true;
        vm.email = {};
        vm.entityTemplate = null;
        vm.emailTemplates = [];
        vm.saveEmail = saveEmail;
        vm.sendEmail = sendEmail;
        vm.reply = reply;
        vm.replyAll = replyAll;
        vm.forward = forward;
        vm.deleteEmail = deleteEmail;
        vm.openConversation = openConversation;
        vm.setEmailTemplate = setEmailTemplate;
        vm.showEmailPreview = showEmailPreview;
        vm.showSelectRecipients = showSelectRecipients;
        vm.viewTitle = '';
        Object.defineProperty(vm, 'titleButtonDisabled', {
            get: getTitleButtonDisabled
        });
        Object.defineProperty(vm, 'closeButtonDisabled', {
            get: getCloseButtonDisabled
        });
        Object.defineProperty(vm, 'showConversationButton', {
            get: function () { return !_.isNil(vm.email) && vm.email.emailPersisted && vm.email.data.Status !== 'Draft'; }
        });
        vm.close = close;


        /* ****************************************************************
         * Loading
         * **************************************************************** */

        activate();

        function activate() {
            WindowTitleService.setTitle(vm.viewTitle);

            loadEmail()
                .then(loadEmailOk, loadEmailError)
                .finally(function () {
                    vm.loading = false;
                    updateViewTitle();
                });

            function loadEmailOk() {
                if (vm.email.data.Status === 'Draft') {
                    return loadEmailEditMode()
                        .catch(handleLoadingError);
                }
            }

            function loadEmailError(error) {
                if (error && error.status === 404) {
                    vm.errorMessage = 'Email not found.';
                } else {
                    handleLoadingError(error);
                }
            }

            function handleLoadingError(error) {
                if (_.isString(error)) {
                    vm.errorMessage = error;
                } else {
                    vm.errorMessage = 'Error loading Email.';
                    DialogService.error('Error loading Email', error);
                }
            }
        }

        /**
         * Load email into vm.email.
         *
         * @returns {Promise} Resolves email object if successfully loaded, rejects if there was an error.
         */
        function loadEmail() {
            var emailPromise = null;

            if (angular.isDefined($routeParams.id) && $routeParams.id !== null) {
                if ($routeParams.id === 'new') {
                    if (angular.isDefined($routeParams.fromEntityIds) && $routeParams.fromEntityIds !== null && $routeParams.fromEntityIds !== '') {
                        emailPromise = Email.getNew($routeParams.fromEntityIds);
                    } else {
                        return $q.reject('The list of entities from which the emails are to be sent must be provided when composing a new email message.');
                    }
                } else {
                    emailPromise = Email.get($routeParams.id);
                }
            }

            if (emailPromise === null) {
                return $q.reject('Email not found.');
            }

            return emailPromise.then(function (email) {
                vm.email = email;

                UnsavedChangesService.register({
                    scope: $scope,
                    unsavedItemName: 'Email',
                    getDirtyFunction: function () { return vm.email.dirty; },
                    onLocationChange: preLocationChangeOperations
                });
            });
        }

        /**
         * Load additional things for email edit mode.
         * 
         * Prerequisite: vm.email is already loaded.
         */
        function loadEmailEditMode() {
            vm.showCc = (angular.isDefined(vm.email.data.Cc) && vm.email.Cc !== null && vm.email.data.Cc.length > 0);
            vm.showBcc = (angular.isDefined(vm.email.data.Bcc) && vm.email.Bcc !== null && vm.email.data.Bcc.length > 0);
            vm.showReplyTo = (angular.isDefined(vm.email.data.ReplyTo) && vm.email.data.ReplyTo !== null && vm.email.data.ReplyTo.length > 0);
            vm.showPreheader = (!_.isNil(vm.email.data.Preheader) && vm.email.data.Preheader !== '');
            vm.showFromAddress = (!_.isNil(vm.email.data.FromAddressOverrideEntityId) && vm.email.data.FromAddressOverrideEntityId !== '');

            var loadEmailEditPromises = [];
            loadEmailEditPromises.push(loadEmailDraftDelay());
            loadEmailEditPromises.push(loadEmailContacts(vm.email.data.MultipleFromMode));
            loadEmailEditPromises.push(loadEmailEntityTemplate());

            return $q.all(loadEmailEditPromises).then(function () {
                $scope.$watch('vm.email.data.From', function (newVal, oldVal) {
                    if (newVal !== oldVal && !vm.email.data.MultipleFromMode) {
                        vm.loading = true;
                        saveEmail()
                            .then(function () {
                                loadEmailContacts(vm.email.data.MultipleFromMode)
                                    .then(function () {
                                    }, function (error) {
                                        DialogService.error('Error loading Email Contacts', error);
                                    });
                            }, function (error) {
                                DialogService.error('Error saving Email draft', error);
                            }).finally(function () {
                                vm.loading = false;
                            });
                    }
                }, true);

                $scope.$on('$destroy', function () {
                    stopSaveTimer();
                    hideSaveToast();
                });

                initialiseSaveTimer();
            });
        }

        /**
         * Load email draft delay into emailDraftDelay.
         *
         * @returns {Promise} A promise resolved once loading is finished, resolves regardless of error.
         */
        function loadEmailDraftDelay() {
            return EmailService.getEmailDraftDelay()
                .then(getEmailDraftDelayOk, getEmailDraftDelayError);

            function getEmailDraftDelayOk(data) {
                emailDraftDelay = data;
            }

            function getEmailDraftDelayError(response) {
                return $q.reject('The email draft delay was unable to be retrieved.');
            }
        }

        /**
         * Load email contacts, that is, receipients that the email may be sent to.
         * These receipients are related to the entity that the email is related to.
         */
        function loadEmailContacts(includeReferenceContacts) {
            if ($routeParams.id === 'new') {
                var fromEntityId = vm.email.primaryEntityId;

                return EntitiesService.getContacts(fromEntityId, includeReferenceContacts, ['Email'])
                        .then(getContactsOk, getContactsError);
            } else {
                return EmailService.getEmailContacts($routeParams.id, includeReferenceContacts)
                        .then(getContactsOk, getContactsError);
            }

            function getContactsOk(data) {
                vm.contacts = _.filter(data, function (contact) {
                    return contact.Usage == 'Email';
                });
            }

            function getContactsError(response) {
                return $q.reject('The email address contacts were unabled to be retrieved.');
            }
        }

        /**
         * Load the entity template associated with the email.
         * This will also load the email templates.
         *
         * @returns {promise}
         */
        function loadEmailEntityTemplate() {
            if (!_.isNil(vm.email.primaryEntityId)) {
                return EntitiesService.get(vm.email.primaryEntityId)
                    .then(function (entity) {
                        vm.entityTemplate = entity.EntityTemplate;

                        return EmailTemplatesService.query({ entityTemplateId: vm.entityTemplate.Id, active: true })
                            .then(function (emailTemplates) {
                                vm.emailTemplates = emailTemplates.Items;

                                return vm.entityTemplate;
                            });
                    });
            } else {
                $q.resolve();
            }
        }


        /* ****************************************************************
         * Saving Drafts
         * **************************************************************** */

        /**
         * Initialises the timer 'saveEmailDraftTimer' which will call the 'draft email save' function. If the timer has already been defined, it will be left
         * as is.
         */
        function initialiseSaveTimer() {
            stopSaveTimer();

            // Only draft emails use the save timer
            if (vm.email.data.Status === 'Draft') {
                saveEmailDraftTimer = $timeout(saveEmail, emailDraftDelay);
            }
        }

        /**
         * Stops the 'saveEmailDraftTimer' if it exists and sets it to be null.
         */
        function stopSaveTimer() {
            if (angular.isDefined(saveEmailDraftTimer) && saveEmailDraftTimer !== null) {
                $timeout.cancel(saveEmailDraftTimer);
                saveEmailDraftTimer = null;
            }
        }


        /**
         * Saves the current email if its value has changed since it was last saved.
         *
         * @param {bool} viewClosing - If this is the 'last' save as part of the view closing process, the response handling logic will change
         */
        function saveEmail(viewClosing) {
            if (vm.email.dirty) {
                return vm.email.save()
                    .then(saveEmailOk, saveEmailError);
            } else if (viewClosing) {
                return $q.when();
            } else {
                initialiseSaveTimer();
            }

            function saveEmailOk(email) {
                vm.email.Id = email.Id;

                if (viewClosing) {
                    return $q.when();
                } else {
                    showSaveSuccessToast();
                    initialiseSaveTimer();
                    updateViewTitle();
               }
            }

            function saveEmailError(response) {
                if (viewClosing) {
                    return DialogService.confirm('An error was encountered when saving the Email, do you still wish to navigate away from the Email?')
                        .then(closeViewConfirmOk, closeViewConfirmCancel);
                } else {
                    showSaveErrorToast(response);
                    initialiseSaveTimer();
                }

                function closeViewConfirmOk() {
                    stopSaveTimer();

                    return $q.when();
                }

                function closeViewConfirmCancel() {
                    showSaveErrorToast(response);
                    initialiseSaveTimer();

                    return $q.reject();
                }
            }
        }

        function hideSaveToast() {
            if (angular.isDefined(vm.currentStatusToast)) {
                $mdToast.hide(vm.currentStatusToast);
            }
        }

        function showSaveSuccessToast() {
            if (vm.email.data.Status === 'Draft') {
                var message = 'Draft email was successfully saved at ' + new Date().toLocaleTimeString();

                vm.currentStatusToast = $mdToast.show({
                    hideDelay: 2000,
                    position: 'bottom right',
                    controller: 'EmailSaveSuccessToast',
                    templateUrl: 'app/email/controls/email-save-success-toast.tmpl.html',
                    locals: { toastText: message }
                });
            }
        }

        function showSaveErrorToast(response) {
            if (vm.email.data.Status === 'Draft') {
                var message = 'Draft email was not successfully saved at ' + new Date().toLocaleTimeString();

                vm.currentStatusToast = $mdToast.show({
                    hideDelay: 0,
                    position: 'bottom right',
                    controller: 'EmailSaveErrorToast',
                    templateUrl: 'app/email/controls/email-save-error-toast.tmpl.html',
                    locals: { toastText: message, errorResponse: response }
                });
            }
        }

        /**
         * If the user tries to navigate to another location, there's some checks that should be made...
         */
        function preLocationChangeOperations() {
            stopSaveTimer();

            var promiseChain;

            // If attachment uploads are in progress, can't proceed unless the uploads are cancelled.
            if (vm.email.Attachments.uploadInProgress) {
                promiseChain = DialogService.confirm('Cancel attachment upload(s)?')
                    .then(vm.email.Attachments.cancelUploadsInProgress);
            } else {
                promiseChain = $q.resolve();
            }

            if (vm.email.dirty) {
                if (vm.email.data.Status === 'Draft') {
                    // Drafts are saved automatically
                    promiseChain = promiseChain.then(function () {
                        return saveEmail(true);
                    });
                } else {
                    // For non-drafts, prompt the user
                    promiseChain = promiseChain.then(function () {
                        return DialogService.unsavedChanges('Email', true)
                            .then(function (emailShouldBeSaved) {
                                if (emailShouldBeSaved) {
                                    return saveEmail(true);
                                }
                            });
                    });
                }
            }

            promiseChain = promiseChain.then(function () {
                // If the user is closing the email edit view, and it's a saved draft, prompt whether they want to keep the draft.
                if (vm.email.data.Status === 'Draft' && vm.email.data.Id) {
                    return ChilliUserProfileService.getUserProfile()
                        .then(function (userProfile) {
                            return DialogService.unsavedChanges('Draft Email', true, '<p>We saved a draft of this email for you.</p><p>Do you want to keep it?</p>');
                        }).then(function (keepEmail) {
                            if (keepEmail) {
                                return $q.resolve();
                            } else {
                                return vm.email.delete()
                                    .catch(function (response) {
                                        return DialogService.error('Error deleting Email', response)
                                            .finally(function () {
                                                return $q.reject(response);
                                            });
                                    });
                            }
                        });
                }
            });

            promiseChain = promiseChain.catch(function () {
                initialiseSaveTimer();
                return $q.reject();
            });

            return promiseChain;
        }
        

        /* **************************************************************** */


        /**
         * Sends the current draft email. As part of this process the email record will be updated to have a status of sent.
         */
        function sendEmail() {
            if (!_.isNil(vm.email.validateEmail) && vm.email.validateEmail.length > 0) {
                DialogService.messageList('Error sending email', vm.email.validateEmail);

            } else {
                hideSaveToast();
                stopSaveTimer();
                UnsavedChangesService.clearRegistration();

                if (!vm.email.data.MultipleFromMode) {
                    vm.email.send()
                       .then(sendEmailOk, sendEmailError);

                    NavigationAidService.goBack();
                } else {
                    showSelectRecipients()
                        .then(function (sendEmails) {
                            vm.email.send(sendEmails)
                                .then(sendEmailOk, sendEmailError);

                            NavigationAidService.goBack();
                        });
                }
            }

            function sendEmailOk(data) {
                if (vm.email.data.MultipleFromMode) {
                    showSendReport(data);
                }
            }

            function sendEmailError(response) {
                DialogService.error("Error encountered while sending Email", response);
            }
        }

        /**
         * Update Title to be shown at the top of the view and browser window/tab title.
         */
        function updateViewTitle() {
            var title = '';

            if (angular.isDefined(vm.email) && angular.isDefined(vm.email.data) && angular.isDefined(vm.email.data.Status)) {
                if (vm.email.data.Status === 'Draft') {
                    vm.showCc = !vm.email.data.MultipleFromMode;
                    if (vm.email.data.MultipleFromMode) {
                        title = 'Create Bulk Email';
                    }
                    else
                        title = 'Create Email';
                } else if (vm.email.data.Status === 'Sent') {
                    title = 'View Email';
                } else {
                    title = vm.email.data.Status + ' Email';
                }
            } else if ($routeParams.id === 'new') {
                title = 'Create Email';
            } else {
                title = 'Email';
            }

            if (!_.isNil(vm.email) && !_.isNil(vm.email.data) && !_.isNil(vm.email.data.Subject) && vm.email.data.Subject.length) {
                title += ' - ' + vm.email.data.Subject;
                title = title.substr(0, 128); // Don't let it be too long
            }

            vm.viewTitle = title;
            WindowTitleService.setTitle(title);
        }

        /**
         * @returns {bool} Is the title button disabled.
         */
        function getTitleButtonDisabled() {
            return (vm.errorMessage || vm.disableAllButtons ||
                (angular.isDefined(vm.email) && vm.email.sendInProgress) ||
                (angular.isDefined(vm.email) && angular.isDefined(vm.email.Attachments) && ((angular.isDefined(vm.email.Attachments.uploadInProgress) && vm.email.Attachments.uploadInProgress) ||
                (angular.isDefined(vm.email.Attachments.attachmentHasError) && vm.email.Attachments.attachmentHasError))));
        }

        /**
         * @returns {bool} Is the close button disabled.
         */
        function getCloseButtonDisabled() {
            return (vm.disableAllButtons ||
                (angular.isDefined(vm.email) && vm.email.sendInProgress));
        }

        /**
         * When the view is 'closed' the timer should be stopped and draft email saved if applicable. Then navigation to a previous page should occur.
         * This handling of the saving will automatically be handled by the unsaved changes service.
         */
        function close() {
            stopSaveTimer();
            NavigationAidService.goBack();
        }

        /**
         * Creates a reply email and redirects to the new email.
         */
        function reply() {
            if (!vm.disableAllButtons) {
                respondToEmail(vm.email, 'Reply');
            }
        }

        /**
         * Creates a reply all email and redirects to the new email.
         */
        function replyAll() {
            if (!vm.disableAllButtons) {
                respondToEmail(vm.email, 'ReplyAll');
            }
        }

        /**
         * Creates a forward email and redirects to the new email.
         */
        function forward() {
            if (!vm.disableAllButtons) {
                respondToEmail(vm.email, 'Forward');
            }
        }

        function deleteEmail() {
            return DialogService.confirm('Delete the Email?', 'Delete Email confirm')
                .then(deleteConfirmed);

            function deleteConfirmed() {
                stopSaveTimer();

                if (vm.email.Attachments.uploadInProgress) {
                    vm.email.Attachments.cancelUploadsInProgress();
                }

                if (vm.email.data.Id) {
                    // Non-zero Id means the email has been saved, so it must be deleted.
                    vm.email.delete()
                        .then(deleteOk, deleteError);
                } else {
                    // Draft email hasn't been saved yet, so no need to delete it.
                    deleteOk();
                }
            }

            function deleteOk() {
                UnsavedChangesService.clearRegistration();
                NavigationAidService.goBack();
            }

            function deleteError(response) {
                DialogService.error('Error deleting Email', response);
                initialiseSaveTimer();
            }
        }

        /**
         * Creates a new response email and redirects to the newly created email upon successful creation.
         */
        function respondToEmail(email, responseType) {
            vm.disableAllButtons = true;

            email.createResponse(responseType)
                .then(function (data) {
                    $location.url('email/' + data);
                }, function (response) {
                    if (response !== 'Cancelled') {
                        DialogService.error('Error creating Email response', response);
                    }
                    vm.disableAllButtons = false;
                });
        }

        /**
         * Opens conversation dialog for the email.
         */
        function openConversation() {
            DialogService.show({
                controller: 'ViewEmailConversationDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/email/view-email-conversation-dialog.tmpl.html',
                locals: { email: vm.email },
                size: DialogService.sizes.extraLarge
            }).then(function (response) {
                if (!_.isNil(response.Email.data.Id)) {
                    switch (response.Action) {
                        case 'View':
                            $location.url('email/' + response.Email.data.Id);
                            break;
                        case 'Reply':
                        case 'ReplyAll':
                        case 'Forward':
                            respondToEmail(response.Email, response.Action);
                            break;
                    }
                }
            });
        }

        /**
         * Opens set email template dialog for the email.
         */
        function setEmailTemplate() {
            DialogService.show({
                controller: 'SetEmailTemplateDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/email/set-email-template-dialog.tmpl.html',
                locals: {
                    email: vm.email,
                    emailTemplates: vm.emailTemplates
                }
            }).then(function (response) {
            });
        }

        /**
         * Opens a dialog showing a 'preview' of the email. This is how the sent email would look.
         */
        function showEmailPreview() {
            DialogService.show({
                controller: 'EmailViewDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/email/email-view-dialog.tmpl.html',
                locals: { email: vm.email },
                size: DialogService.sizes.extraLarge
            });
        }

        /**
         * Opens a dialog showing a send report for the email.
         */
        function showSendReport(emailSendReport) {
            return DialogService.show({
                controller: 'SendReportDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/controls/dialog/send-report-dialog.tmpl.html',
                locals: { sendReports: emailSendReport, reportType: 'Email' },
                size: DialogService.sizes.extraLarge
            });
        }

        /**
         * Opens a dialog showing a recipients selector for the email.
         *
         * @param {bool} viewOnlyMode - Should the dialog be shown in view only mode
         */
        function showSelectRecipients(viewOnlyMode) {
            return DialogService.show({
                controller: 'EmailSelectRecipientsDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/email/email-select-recipients-dialog.tmpl.html',
                locals: { email: vm.email, viewOnlyMode: viewOnlyMode },
                size: DialogService.sizes.large
            });
        }
    }
})();
